import type { FC } from 'react';
import React from 'react';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { AgentCard } from './AgentCard.tsx';
import type { CardLayout } from './AgentCard.tsx';
import type { AgentCardConfig } from './agentCards.config.ts';

const rowStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.200'),
	width: '100%',
});

/**
 * Number of visible cards at or below which each card switches to a horizontal
 * layout (preview reflows bottom → right). With 3 cards every card stays
 * vertical; with 1 or 2 cards the extra width is used for a horizontal preview.
 */
const HORIZONTAL_LAYOUT_THRESHOLD = 2;

export const getCardLayout = (visibleCount: number): CardLayout =>
	visibleCount <= HORIZONTAL_LAYOUT_THRESHOLD ? 'horizontal' : 'vertical';

export type AgentCardRowProps = {
	cards: AgentCardConfig[];
	/** Called with a card's `key` when its dismiss "×" is clicked. */
	onDismiss?: (key: string) => void;
};

/**
 * Responsive row of "Rovo Agents for you" cards.
 *
 * - 3 cards: each card fills an equal third of the row (vertical layout).
 * - 2 / 1 cards: remaining cards stretch to fill the row and switch to a
 *   horizontal layout so the preview thumbnail reflows from bottom to right.
 *
 * The row is presentational: it reflows for whatever cards it is given. The
 * visible set (and dismiss handling) is owned by the parent section.
 */
export const AgentCardRow: FC<AgentCardRowProps> = ({ cards, onDismiss }) => {
	if (cards.length === 0) {
		return null;
	}

	const layout = getCardLayout(cards.length);

	return (
		<div css={rowStyles}>
			{cards.map((card) => (
				<AgentCard
					key={card.key}
					card={card}
					layout={layout}
					onDismiss={onDismiss ? () => onDismiss(card.key) : undefined}
				/>
			))}
		</div>
	);
};
